import type { FC } from 'react';
import { Suspense, useCallback, useState } from 'react';
import type { ActionFieldsOptions, ActionIndexProps } from '../../types';
import { observer } from 'mobx-react-lite';
import useCurrentTasksFormStore from '../../hooks/useCurrentTasksFormStore';
import actionFieldsVisibility from '../../utils/actionFieldsVisibility';
import { ConditionFormContainer } from '../../components/RuleEditComponents';
import { ActionFormContainerTitle, SuspenseFallbackSkeleton } from '../../components/RuleEditTasksFormComponents';
import { Box } from '@mui/material';
import ActionType from '../../components/actions/ActionType';
import ActionTypeFieldSelect from '../../components/actions/ActionTypeFieldSelect';
import ActionMetrics from '../../components/actions/ActionMetrics';
import ActionValue from '../../components/actions/ActionValue';
import ActionUnitValue from '../../components/actions/ActionUnitValue';
import ActionReplaceNameFields from '../../components/actions/ActionReplaceNameFields';
import ActionCountValueField from '../../components/actions/ActionCountValueField';
import SubActionValueField from '../../components/actions/SubActionValueField';

const ActionEntry: FC<ActionIndexProps> = observer(({ indexTask, indexVariant, indexAction }) => {
  const isVariant = typeof indexVariant === 'number';
  const tasksFormStore = useCurrentTasksFormStore();
  const storedActionValue = tasksFormStore.getActionValues({ indexTask, indexAction, indexVariant })?.action;
  const {
    isValueVisible,
    isOnlyStringValue,
    isReplaceInName,
    isSubActionVisible,
    isValueToggleVisible,
    isSingleActionValue,
  } = actionFieldsVisibility(storedActionValue as string);
  const storedValue = tasksFormStore.getActionValues({ indexTask, indexAction, indexVariant }).value;

  const [actionFieldsOptions, setActionFieldsOptions] = useState<ActionFieldsOptions>(() =>
    typeof storedValue === 'string' && isNaN(Number(storedValue)) && !storedValue.includes('{{') ? 'metric' : 'absolute'
  );

  const handleIsMetricValueField = useCallback(
    (value: ActionFieldsOptions) => {
      tasksFormStore.setActionFieldValue({
        indexTask,
        indexAction,
        indexVariant,
        field: 'unit',
        value: 'absolute',
      });
      tasksFormStore.setActionFieldValue({
        indexTask,
        indexAction,
        indexVariant,
        field: 'value',
        value: '',
      });

      setActionFieldsOptions(value);
    },
    [tasksFormStore, indexVariant, indexTask, indexAction, setActionFieldsOptions]
  );

  return (
    <ConditionFormContainer
      type="action"
      indexCondition={indexAction}
      title={
        <ActionFormContainerTitle
          title={`Action ${indexAction + 1}`}
          indexAction={indexAction}
          indexTask={indexTask}
          indexVariant={indexVariant}
        />
      }
      sx={{ p: 0 }}
    >
      <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <Box
            sx={{
              flex: isReplaceInName ? '0 0 40%' : isSingleActionValue ? 1 : '0 0 40%',
              borderBottom: isSubActionVisible ? `1px solid ${`rgba(0, 8, 46, 0.12)`}` : 'none',
              borderRight: isValueVisible ? `1px solid ${`rgba(0, 8, 46, 0.12)`}` : 'none',
            }}
          >
            <Suspense fallback={<SuspenseFallbackSkeleton />}>
              <ActionType
                indexAction={indexAction}
                indexTask={indexTask}
                indexVariant={indexVariant}
                field={'action'}
              />
            </Suspense>
          </Box>
          {isValueVisible && (
            <>
              {!isOnlyStringValue && isValueToggleVisible && (
                <Box
                  sx={{
                    flex: '0 0 80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottom: isSubActionVisible ? `1px solid ${`rgba(0, 8, 46, 0.12)`}` : 'none',
                    borderRight: `1px solid ${`rgba(0, 8, 46, 0.12)`}`,
                  }}
                >
                  <ActionTypeFieldSelect
                    disabled={isVariant}
                    onChange={handleIsMetricValueField}
                    value={actionFieldsOptions}
                  />
                </Box>
              )}
              <Box sx={{ flex: 1, borderBottom: isSubActionVisible ? `1px solid ${`rgba(0, 8, 46, 0.12)`}` : 'none' }}>
                {actionFieldsOptions === 'metric' && !isOnlyStringValue ? (
                  <Suspense fallback={<SuspenseFallbackSkeleton />}>
                    <ActionMetrics
                      indexAction={indexAction}
                      indexTask={indexTask}
                      indexVariant={indexVariant}
                      field="value"
                    />
                  </Suspense>
                ) : (
                  <ActionValue
                    indexAction={indexAction}
                    indexTask={indexTask}
                    indexVariant={indexVariant}
                    field="value"
                    type="string"
                  />
                )}
              </Box>
              {actionFieldsOptions === 'absolute' && (
                <ActionUnitValue indexAction={indexAction} indexTask={indexTask} indexVariant={indexVariant} />
              )}
            </>
          )}
          {!isValueVisible && isReplaceInName && (
            <ActionReplaceNameFields indexAction={indexAction} indexTask={indexTask} indexVariant={indexVariant} />
          )}
        </Box>

        <ActionCountValueField indexAction={indexAction} indexTask={indexTask} indexVariant={indexVariant} />
        <SubActionValueField indexAction={indexAction} indexTask={indexTask} indexVariant={indexVariant} />
      </Box>
    </ConditionFormContainer>
  );
});

export default ActionEntry;
